import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import ZoomableImage from "@/components/ZoomableImage";

const STATUS_STYLES = {
  pending: "bg-amber-600/20 text-amber-400 border-amber-600/40",
  approved: "bg-emerald-600/20 text-emerald-400 border-emerald-600/40",
  rejected: "bg-rose-600/20 text-rose-400 border-rose-600/40",
};
const STATUS_LABELS = { pending: "Beklemede", approved: "Onaylı", rejected: "Reddedildi" };

export default function Mugshot() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", info: "", image_url: "" });
  const [reviewOpen, setReviewOpen] = useState(null);

  const canApprove = me?.is_officer_manager;

  const load = () => api.get("/memur/mugshotlar").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/memur/me").then(r => setMe(r.data));
  }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.image_url) return toast.error("Başlık ve fotoğraf gerekli");
    try {
      await api.post("/memur/mugshotlar", form);
      toast.success("Mugshot gönderildi");
      setOpen(false); setForm({ title: "", info: "", image_url: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const review = async (status) => {
    try {
      await api.post(`/memur/mugshotlar/${reviewOpen.id}/review`, { status });
      toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
      setReviewOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const filtered = (status) => items.filter(i => status === "all" || i.status === status);

  const renderGrid = (list) => (
    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
      {list.length === 0 && (
        <Card className="bg-zinc-950 border-zinc-900 p-10 text-center sm:col-span-2 md:col-span-3">
          <p className="text-zinc-500 text-sm">Kayıt bulunamadı.</p>
        </Card>
      )}
      {list.map(m => (
        <Card key={m.id} className="bg-zinc-950 border-zinc-900 overflow-hidden" data-testid={`mugshot-${m.id}`}>
          <ZoomableImage src={m.image_url} alt={m.title} className="w-full h-48 object-cover" />
          <div className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">{m.title}</span>
              <span className={`text-[10px] font-mono px-2 py-0.5 border ${STATUS_STYLES[m.status]}`}>{STATUS_LABELS[m.status]}</span>
            </div>
            <p className="text-zinc-400 text-xs whitespace-pre-wrap">{m.info}</p>
            <div className="text-[10px] font-mono text-zinc-600">{m.officer_name} · {new Date(m.created_at).toLocaleString("tr-TR")}</div>
            <div className="text-[10px] font-mono text-zinc-600">72 saat sonra otomatik silinir</div>
            {canApprove && m.status === "pending" && (
              <Button size="sm" onClick={() => setReviewOpen(m)} data-testid={`review-mugshot-${m.id}`} className="bg-rose-600 hover:bg-rose-700 w-full">İncele</Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// POLİS DEPARTMANI</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Mugshot</h1>
          <p className="text-zinc-500 mt-2">Fotoğraflar 72 saat sonra otomatik silinir.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-mugshot-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2" />Yeni Mugshot</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle className="font-heading">Yeni Mugshot</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-mono">BAŞLIK</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="mugshot-title-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs font-mono">BİLGİ</Label>
                <Textarea rows={4} value={form.info} onChange={e => setForm({ ...form, info: e.target.value })} data-testid="mugshot-info-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs font-mono">FOTOĞRAF</Label>
                <div className="mt-1.5"><ImageUploader value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} multiple={false} label="Fotoğraf Yükle" /></div></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="mugshot-submit-button" className="bg-rose-600 hover:bg-rose-700">Gönder</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Tabs defaultValue="pending">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected">Red</TabsTrigger>
          <TabsTrigger value="all">Tümü</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">{renderGrid(filtered("pending"))}</TabsContent>
        <TabsContent value="approved" className="mt-4">{renderGrid(filtered("approved"))}</TabsContent>
        <TabsContent value="rejected" className="mt-4">{renderGrid(filtered("rejected"))}</TabsContent>
        <TabsContent value="all" className="mt-4">{renderGrid(filtered("all"))}</TabsContent>
      </Tabs>

      <Dialog open={!!reviewOpen} onOpenChange={v => !v && setReviewOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-heading">Mugshotu İncele</DialogTitle></DialogHeader>
          {reviewOpen && (
            <div className="space-y-3">
              <ZoomableImage src={reviewOpen.image_url} className="w-full h-48 object-cover border border-zinc-800" />
              <div className="font-semibold">{reviewOpen.title}</div>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{reviewOpen.info}</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={() => review("rejected")} data-testid="reject-mugshot-button" variant="outline" className="border-rose-600/40 text-rose-400 hover:bg-rose-950/30"><X className="w-4 h-4 mr-1" />Reddet</Button>
            <Button onClick={() => review("approved")} data-testid="approve-mugshot-button" className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" />Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
